'use client'

import { useCallback, useMemo } from 'react'

import { buildCreditsLimitConfig } from '@/components/upgrade/upgrade-limit-config'
import { useOptionalUpgradeLimit } from '@/contexts/upgrade-limit-context'
import { useUsageLimitsOptional } from '@/contexts/usage-limits-context'
import { type BillingPlanKey, isBillingPlanKey } from '@/lib/billing/plans'

/**
 * Typed error thrown when an in-flight code path discovers the org is out
 * of credits AFTER the action has started (e.g. the chat runtime's
 * `sendRequest`, an element-edit dispatcher mid-flight). Callers that
 * own UI state (queues, optimistic message lists, skeletons) check for
 * this instance and revert their pending state instead of treating it as
 * a generic failure.
 *
 * Surfaces are expected to ALSO pre-gate at the user-action entry point
 * (e.g. `useCreditsLimitGuard().guard(...)` wrapping the click handler)
 * so this error path is the defense-in-depth, not the primary UX.
 */
export class CreditsLimitBlockedError extends Error {
  readonly kind = 'credits_limit_blocked' as const
  constructor(message = 'Out of AI credits — upgrade to keep editing.') {
    super(message)
    this.name = 'CreditsLimitBlockedError'
  }
}

export function isCreditsLimitBlockedError(
  error: unknown
): error is CreditsLimitBlockedError {
  return (
    error instanceof Error &&
    (error as { kind?: string }).kind === 'credits_limit_blocked'
  )
}

export type CreditsLimitStatus = {
  remainingCredits: number
  includedCredits: number
  isOverLimit: boolean
  planKey: BillingPlanKey
  /** Plans with no fixed credit pool (Enterprise) — never gated client-side. */
  isUnlimitedPlan: boolean
}

export type UseCreditsLimitGuardResult = {
  /**
   * Run `onAllowed` if the org still has credits. When over-limit, opens the
   * upgrade dialog with the credits-specific config and does NOT call
   * `onAllowed`.
   */
  guard: (onAllowed: () => void) => void
  status: CreditsLimitStatus | null
  isLoading: boolean
  /** Convenience flag for `disabled={isOverLimit}` on submit affordances. */
  isOverLimit: boolean
}

function isUnlimitedPlanKey(planKey: BillingPlanKey): boolean {
  return planKey === 'enterprise'
}

/**
 * Hook that gates AI generation actions behind the per-org credit budget.
 *
 * Behavior:
 *   - When the upgrade-dialog provider isn't mounted (e.g. on public pages),
 *     the guard is a pass-through that always runs `onAllowed`.
 *   - When usage data is still loading, the guard runs `onAllowed`
 *     optimistically. The Convex `assertCanSpendCredits` query and the API
 *     route's `checkOrgCredits` call are the safety net and will reject
 *     with a typed `LIMIT_REACHED` error if the user is actually over-limit.
 *   - Enterprise plans are never gated client-side (they have custom volume
 *     and the server-side gate short-circuits to "unlimited" too).
 *   - When `remainingCredits <= 0`, opens the same `UpgradeLimitDialog`
 *     used for brand and domain limits, with a credits-specific config
 *     generated by `buildCreditsLimitConfig`.
 */
export function useCreditsLimitGuard(): UseCreditsLimitGuardResult {
  const usage = useUsageLimitsOptional()
  const upgrade = useOptionalUpgradeLimit()

  const status = useMemo<CreditsLimitStatus | null>(() => {
    if (!usage || usage.isLoading) {
      return null
    }
    const planKey = isBillingPlanKey(usage.planKey)
      ? (usage.planKey as BillingPlanKey)
      : null
    if (planKey === null) {
      return null
    }
    // `remainingCredits` is allowed to be NEGATIVE when an org has
    // overdrafted (e.g. parallel requests racing past the gate, or a
    // single request that consumed more than the pre-flight check
    // expected). Surfaces consume the honest value so they can show the
    // deficit. `isOverLimit` correctly captures both 0 and negatives via
    // `<= 0`, so the gate behavior is unchanged.
    const remainingCredits = usage.remainingCredits
    // Period grant, NOT the monthly catalog: a prepaid multi-month term
    // stores several months of allowance in one period, and this figure is
    // rendered verbatim as the dialog's "X / X used".
    const includedCredits = usage.includedCredits + (usage.rolloverCredits ?? 0)
    const isUnlimited = isUnlimitedPlanKey(planKey)
    return {
      remainingCredits,
      includedCredits,
      isOverLimit: !isUnlimited && remainingCredits <= 0,
      planKey,
      isUnlimitedPlan: isUnlimited,
    }
  }, [usage])

  const isLoading = usage?.isLoading ?? true
  const isOverLimit = status?.isOverLimit ?? false

  const guard = useCallback(
    (onAllowed: () => void) => {
      // No status yet, no provider, or loading -> defer to server enforcement.
      if (!status || !upgrade) {
        onAllowed()
        return
      }

      if (status.isUnlimitedPlan || !status.isOverLimit) {
        onAllowed()
        return
      }

      upgrade.showUpgrade(
        buildCreditsLimitConfig({
          currentPlanKey: status.planKey,
          // For the dialog we display total included credits as the "limit"
          // so the message reads "X / X used", not "X / 0".
          used: status.includedCredits,
          limit: status.includedCredits,
        })
      )
    },
    [status, upgrade]
  )

  return { guard, status, isLoading, isOverLimit }
}
